"use client";

import Image from "next/image";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Pause, Play } from "lucide-react";
import { t } from "@/content/i18n/messages";
import type { Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { intelligentUIStory } from "@/content/chatgpt-intelligent-ui";
import styles from "./IntelligentUIExperience.module.css";

const IMAGE_ROOT = "/images/chatgpt-intelligent-ui";
const CHATGPT_URL = "https://chatgpt.com/";
const focusClass = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4";

export function IntelligentUIStory({ locale, children }: { locale: Locale; children: ReactNode }) {
  // Explore is also the server-rendered default, matching the selected artwork.
  const [active, setActive] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const storyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Keep the site's floating contact controls from covering the artwork or inputs.
    const targets = [storyRef.current, document.getElementById("examples")].filter((element): element is HTMLElement => element !== null);
    const visible = new Set<Element>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      }
      document.body.toggleAttribute("data-hide-fabs", visible.size > 0);
    });
    targets.forEach((element) => observer.observe(element));
    return () => {
      observer.disconnect();
      document.body.removeAttribute("data-hide-fabs");
    };
  }, []);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      setReducedMotion(preference.matches);
      setPlaying(!preference.matches);
    };
    sync();
    preference.addEventListener("change", sync);
    return () => preference.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!playing || reducedMotion) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setActive((current) => (current + 1) % intelligentUIStory.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [playing, reducedMotion]);

  return (
    <div ref={storyRef}>
      <div className={styles.hero}>
      <div className={styles.heroCopy}>{children}</div>
      <div className={styles.scene} aria-label={t(locale, "A question becomes an interactive answer")}>
        {intelligentUIStory.map((stage, index) => (
          <div
            key={stage.image}
            aria-hidden={index !== active}
            className={cn(styles.sceneLayer, index === active && styles.sceneActive, playing && styles.sceneMoving)}
          >
            <Image
              src={`${IMAGE_ROOT}/${stage.image}.webp`}
              alt={index === active ? t(locale, stage.alt) : ""}
              width={1337}
              height={1176}
              sizes="(min-width: 1024px) 62vw, 100vw"
              priority
              className="h-full w-full object-cover"
            />
          </div>
        ))}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
          <span className="rounded-md bg-background/95 px-3 py-2 text-xs text-neutral-muted">
            {t(locale, "Illustrative example")}
          </span>
          <button
            type="button"
            disabled={reducedMotion}
            aria-pressed={playing}
            onClick={() => setPlaying((current) => !current)}
            className={cn("inline-flex min-h-11 items-center gap-2 rounded-md bg-background/95 px-3 text-xs font-semibold text-neutral-emphasis disabled:cursor-default", focusClass)}
          >
            {playing ? <Pause size={14} aria-hidden /> : <Play size={14} aria-hidden />}
            {t(locale, reducedMotion ? "Animation off" : playing ? "Pause story" : "Play story")}
          </button>
        </div>
      </div>
      </div>
      <div className="mx-auto grid max-w-4xl grid-cols-3 gap-2 px-4 py-6 sm:gap-8 lg:py-8" aria-label={t(locale, "Explore the three steps")}>
        {intelligentUIStory.map((stage, index) => (
          <button
            key={stage.title}
            type="button"
            aria-pressed={active === index}
            onClick={() => { setActive(index); setPlaying(false); }}
            className={cn("flex min-h-20 items-start gap-3 rounded-lg px-2 py-3 text-left transition-colors sm:items-center", active === index ? "text-primary" : "text-neutral-muted hover:text-primary", focusClass)}
          >
            <span className={cn("hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 font-mono text-sm sm:flex", active === index ? "border-primary bg-primary text-primary-foreground" : "border-border")}>{index + 1}</span>
            <span><span className="block text-sm font-semibold">{t(locale, stage.title)}</span><span className="mt-1 block text-xs leading-relaxed text-neutral-muted">{t(locale, stage.description)}</span></span>
          </button>
        ))}
      </div>
    </div>
  );
}

export { PenangUISimulation as IntelligentUIExamples } from "./PenangUISimulation";
